import { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useNotificationCount, useFollowUpToasts } from "@/hooks/use-notifications";
import { usePermissions } from "@/hooks/use-permissions";
import { Permission } from "@/lib/permissions";
import { useTicketDock } from "@/components/shared/tickets/ticket-dock-params";
import { NotificationList } from "./notification-list";

/**
 * The bell in the top bar, for every signed-in admin: how many things want
 * them (unread notifications plus follow-ups that have come due) and, on
 * click, a popover with both. Same shape as the WhatsApp and Tickets badges;
 * the count is polled every 30 s and TanStack pauses it in a hidden tab.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const count = useNotificationCount();
  const { can } = usePermissions();
  const dock = useTicketDock();
  const canOpenTickets = can(Permission.TICKETS_VIEW);

  useFollowUpToasts(canOpenTickets ? count.data?.followUpsDue : undefined, (ticketId) =>
    dock.open({ kind: "ticket", id: ticketId }),
  );

  const unread = count.data?.unread ?? 0;
  const due = count.data?.followUpsDue ?? 0;
  const total = unread + due;
  const label =
    total > 0
      ? `Notifications — ${[
          unread > 0 ? `${unread} unread` : null,
          due > 0 ? `${due} ${due === 1 ? "follow-up" : "follow-ups"} due` : null,
        ]
          .filter(Boolean)
          .join(" · ")}`
      : "Notifications";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="relative"
              aria-label={label}
            >
              <Bell aria-hidden="true" />
              {total > 0 ? (
                <span
                  aria-hidden="true"
                  className={
                    due > 0
                      ? "absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-xs font-semibold tabular-nums leading-none text-destructive-foreground"
                      : "absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold tabular-nums leading-none text-primary-foreground"
                  }
                >
                  {total > 99 ? "99+" : total}
                </span>
              ) : null}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={14}
        collisionPadding={8}
        // Phone: nearly the whole width; from sm up a 24rem panel.
        className="flex max-h-[min(34rem,var(--radix-popover-content-available-height))] w-[calc(100vw-1rem)] flex-col sm:w-96"
      >
        {open ? (
          <NotificationList
            canOpenTickets={canOpenTickets}
            onNavigate={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
